import { useTranslation } from "react-i18next";

import useCommandText from "./useCommandText";
import type { DashcamStreamData } from "./useDashcamStream";

export interface DashcamStatusText {
  title: string;
  detail: string | null;
  waiting: boolean;
}

export function useDashcamStatusText(data: DashcamStreamData): DashcamStatusText {
  const { t } = useTranslation("common");
  const commandText = useCommandText();

  const statusLabel = (status: string) =>
    t([`monitoreoIa.dashcam.command.status.${status}`, `commands.status.${status}`], {
      defaultValue: t("monitoreoIa.dashcam.command.status.sending"),
    });

  const streamDetail = (error: string) => {
    const [key, code] = error.split(":");
    return t(`monitoreoIa.dashcam.errors.${key}`, { code: code ?? "", defaultValue: error });
  };

  const detailFor = (error: string | null) =>
    error ? (commandText.reason(error) ?? streamDetail(error)) : null;

  if (data.readOnly)
    return {
      waiting: false,
      title: t("monitoreoIa.dashcam.readOnly.title"),
      detail: t("monitoreoIa.dashcam.readOnly.detail"),
    };

  if (data.commandPhase === "failed")
    return {
      waiting: false,
      title: data.commandStatus
        ? statusLabel(data.commandStatus)
        : t("monitoreoIa.dashcam.command.failed"),
      detail: detailFor(data.error),
    };

  if (data.commandPhase !== "ready")
    return {
      waiting: true,
      title: t("monitoreoIa.dashcam.command.turningOn"),
      detail: statusLabel(data.commandStatus ?? "sending"),
    };

  return {
    waiting: false,
    title: t(`monitoreoIa.dashcam.connection.${data.connectionState}`),
    detail: detailFor(data.error),
  };
}

export default useDashcamStatusText;
